import { WorkspaceFolder } from 'vscode-languageserver-protocol';
import { createInterfaceId, Injectable } from '@gitlab-org/di';
import { DuoProject, DuoProjectAccessCache } from './project_access_cache';

export enum DuoProjectStatus {
  DuoEnabled = 'duo-enabled',
  DuoDisabled = 'duo-disabled',
  NonGitlabProject = 'non-gitlab-project',
}

export interface DuoProjectAccessChecker {
  checkProjectStatus(
    uri: string,
    workspaceFolder: WorkspaceFolder,
  ): {
    project?: DuoProject;
    status: DuoProjectStatus;
  };
}

export const DuoProjectAccessChecker =
  createInterfaceId<DuoProjectAccessChecker>('DuoProjectAccessChecker');

@Injectable(DuoProjectAccessChecker, [DuoProjectAccessCache])
export class DefaultDuoProjectAccessChecker {
  #projectAccessCache: DuoProjectAccessCache;

  constructor(projectAccessCache: DuoProjectAccessCache) {
    this.#projectAccessCache = projectAccessCache;
  }

  /**
   * Check if Duo features are enabled for a given DocumentUri.
   *
   * We match the DocumentUri to the project with the longest path.
   * The enabled projects come from the `DuoProjectAccessCache`.
   *
   * @reference `DocumentUri` is the URI of the document to check. Comes from
   * the `TextDocument` object.
   */
  checkProjectStatus(
    uri: string,
    workspaceFolder: WorkspaceFolder,
  ): {
    project?: DuoProject;
    status: DuoProjectStatus;
  } {
    const projects = this.#projectAccessCache.getProjectsForWorkspaceFolder(workspaceFolder);
    if (projects.length === 0) {
      return { status: DuoProjectStatus.NonGitlabProject };
    }
    const project = this.#findDeepestProjectByPath(uri, projects);

    if (!project) {
      return { status: DuoProjectStatus.NonGitlabProject };
    }
    return {
      project,
      status: project.enabled ? DuoProjectStatus.DuoEnabled : DuoProjectStatus.DuoDisabled,
    };
  }

  #findDeepestProjectByPath(uri: string, projects: DuoProject[]): DuoProject | undefined {
    let deepestProject: DuoProject | undefined;
    for (const project of projects) {
      if (uri.startsWith(project.uri.replace('.git/config', ''))) {
        if (!deepestProject || project.uri.length > deepestProject.uri.length) {
          deepestProject = project;
        }
      }
    }

    return deepestProject;
  }
}
